import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { getToken } from "../lib/authToken";
import { CloudNavBar } from "../components/CloudNavBar";
import { ChevronDown, ChevronRight } from "@ensafely/checker-ui";

interface EdgeInstance {
  id: string;
  region: string;
  status: "connected" | "stale" | "disconnected";
  version: string | null;
  last_heartbeat_at: string | null;
  remote_addr: string | null;
  created_at: string;
}

interface EdgeInstancesResponse {
  // API returns "edge_instances" key (not "instances")
  edge_instances: EdgeInstance[];
  quota: {
    // API returns "current" key (not "used")
    current: number;
    limit: number;
  };
}

interface SetupInstructions {
  saas_url: string;
  docker_command: string;
  binary_command: string;
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return "offline";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return "just now";
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function StatusBadge({ status }: { status: EdgeInstance["status"] }) {
  const styles = {
    connected: "bg-green-100 text-green-800 border-green-200",
    stale: "bg-yellow-100 text-yellow-800 border-yellow-200",
    disconnected: "bg-gray-100 text-gray-600 border-gray-200",
  };
  const dots = {
    connected: "bg-green-500",
    stale: "bg-yellow-500",
    disconnected: "bg-gray-400",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dots[status]}`} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function SetupInstructionsCard({ instructions }: { instructions: SetupInstructions | null }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-card rounded-lg shadow mb-6">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/30 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="text-base font-semibold">Setup Instructions</span>
        </div>
        <span className="text-xs text-muted-foreground">{expanded ? "Collapse" : "Expand"}</span>
      </button>

      {expanded && (
        <div className="px-6 pb-6 space-y-4 border-t">
          <p className="text-sm text-muted-foreground pt-4">
            Deploy the edge checker binary or Docker container at your location. Use an API key
            to authenticate — create one on the{" "}
            <Link
              to="/account/api-keys"
              className="text-primary underline hover:no-underline"
            >
              API Keys page
            </Link>
            .
          </p>

          {instructions ? (
            <>
              <div className="space-y-2">
                <p className="text-sm font-medium">Docker</p>
                <pre className="bg-muted rounded p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
                  {instructions.docker_command}
                </pre>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium">Binary</p>
                <pre className="bg-muted rounded p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
                  {instructions.binary_command}
                </pre>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-sm font-medium">Docker</p>
                <pre className="bg-muted rounded p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
                  {`docker run -d \\
  -e CHECKER_SAAS_URL=wss://${window.location.host}/ws/edge \\
  -e CHECKER_API_KEY=ck_your_api_key \\
  -e CHECKER_EDGE_REGION=my-location \\
  ghcr.io/yourorg/checker-edge:latest`}
                </pre>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium">Binary</p>
                <pre className="bg-muted rounded p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
                  {`CHECKER_SAAS_URL=wss://${window.location.host}/ws/edge \\
CHECKER_API_KEY=ck_your_api_key \\
CHECKER_EDGE_REGION=my-location \\
./checker-edge`}
                </pre>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function EdgeInstancesPage() {
  const [instances, setInstances] = useState<EdgeInstance[]>([]);
  const [quota, setQuota] = useState<{ used: number; limit: number } | null>(null);
  const [instructions, setInstructions] = useState<SetupInstructions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchInstances = useCallback(() => {
    return api
      .get<EdgeInstancesResponse>("/edge-instances")
      .then((res) => {
        setInstances(res.edge_instances ?? []);
        setQuota(res.quota ? { used: res.quota.current, limit: res.quota.limit } : null);
      })
      .catch((e) => setError(e?.message || "Failed to load edge instances"));
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchInstances(),
      api
        .get<SetupInstructions>("/edge-instances/setup")
        .then((res) => setInstructions(res))
        .catch(() => {
          // Non-critical — show fallback instructions
        }),
    ]).finally(() => setLoading(false));
  }, [fetchInstances]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      fetchInstances();
    }, 30_000);
    return () => clearInterval(timer);
  }, [fetchInstances]);

  // WebSocket real-time updates
  useEffect(() => {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const token = getToken();
    if (!token) return;

    const url = `${proto}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;
    let ws: WebSocket | null = null;
    let shouldReconnect = true;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    function connect() {
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "edge_connected") {
            setInstances((prev) => {
              const exists = prev.find((i) => i.id === msg.instance?.id);
              if (exists) {
                return prev.map((i) =>
                  i.id === msg.instance?.id ? { ...i, ...msg.instance, status: "connected" } : i
                );
              }
              return msg.instance ? [msg.instance, ...prev] : prev;
            });
            if (msg.instance) {
              setQuota((q) => q ? { ...q, used: q.used + 1 } : q);
            }
          } else if (msg.type === "edge_disconnected") {
            setInstances((prev) =>
              prev.map((i) =>
                i.id === msg.instance_id ? { ...i, status: "disconnected" } : i
              )
            );
          } else if (msg.type === "edge_stale") {
            setInstances((prev) =>
              prev.map((i) =>
                i.id === msg.instance_id ? { ...i, status: "stale" } : i
              )
            );
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        scheduleReconnect();
      };

      ws.onerror = () => {
        ws?.close();
      };
    }

    function scheduleReconnect() {
      if (!shouldReconnect) return;
      reconnectTimer = setTimeout(connect, 5000);
    }

    connect();

    return () => {
      shouldReconnect = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, []);

  const handleDelete = async (id: string, region: string) => {
    if (!confirm(`Delete edge instance "${region}"? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await api.del(`/edge-instances/${id}`);
      setInstances((prev) => prev.filter((i) => i.id !== id));
      setQuota((q) => (q ? { ...q, used: Math.max(0, q.used - 1) } : q));
    } catch (e: any) {
      alert(e?.message || "Failed to delete edge instance");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CloudNavBar />
      <div className="max-w-5xl mx-auto py-8 px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold">Edge Instances</h1>
          {quota && (
            <span className="text-sm text-muted-foreground bg-muted px-3 py-1 rounded-full">
              Quota: {quota.used}/{quota.limit} used
            </span>
          )}
        </div>

        {/* Info banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-6 text-sm text-blue-800">
          Edge checkers run health checks from your own infrastructure and report results to the
          platform. Create an API key, then deploy the edge binary with the setup instructions
          below.
        </div>

        {/* Setup Instructions */}
        <SetupInstructionsCard instructions={instructions} />

        {/* Edge Instances Table */}
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-base font-semibold">Connected Edges</h2>
          </div>

          {error && <p className="text-sm text-red-600 p-4">{error}</p>}

          {loading ? (
            <p className="text-sm text-muted-foreground p-6">Loading...</p>
          ) : instances.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6">
              No edge instances connected. Follow the setup instructions above.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">Region</th>
                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">Version</th>
                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">
                    Last Heartbeat
                  </th>
                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">IP</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {instances.map((instance) => (
                  <tr key={instance.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-3 font-medium">{instance.region}</td>
                    <td className="px-6 py-3">
                      <StatusBadge status={instance.status} />
                    </td>
                    <td className="px-6 py-3 text-muted-foreground font-mono text-xs">
                      {instance.version ?? "—"}
                    </td>
                    <td className="px-6 py-3 text-muted-foreground">
                      {formatRelativeTime(instance.last_heartbeat_at)}
                    </td>
                    <td className="px-6 py-3 text-muted-foreground font-mono text-xs">
                      {instance.remote_addr ?? "—"}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <button
                        onClick={() => handleDelete(instance.id, instance.region)}
                        disabled={deletingId === instance.id}
                        className="text-red-600 hover:underline text-xs disabled:opacity-50"
                      >
                        {deletingId === instance.id ? "Deleting..." : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
